import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { TypedMemo } from '../../common/types';
import { PresaveCampaignStats } from '../../../redux/api/fymAPI.types';
import { PresaveCounterCard } from './PresaveCounterCard';
import { daysBetween } from './presaveDates';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole panel; each part uses its own slice of it */
import styles from './PresavePanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PresaveReleasedSummaryProps {
  stats: PresaveCampaignStats;
  releaseDate: string;
}

/**
 * After the release the numbers stop being a promise and become a result, so
 * the panel leads with the delivery instead of a dead countdown.
 */
const PresaveReleasedSummaryComponent: React.FC<PresaveReleasedSummaryProps> = ({ stats, releaseDate }) => {
  const intl = useIntl();

  const total = useMemo(() => stats.byService.reduce((sum, row) => sum + row.count, 0), [stats.byService]);
  const deliveredShare = total > 0 ? stats.executedCount / total : null;

  const headline =
    stats.executedCount > 0
      ? intl.formatMessage(
          { id: 'app.sml.presave.released.headline', defaultMessage: '{count} libraries, delivered' },
          { count: intl.formatNumber(stats.executedCount) }
        )
      : intl.formatMessage(
          { id: 'app.sml.presave.released.headline_emails', defaultMessage: '{count} fans emailed' },
          { count: intl.formatNumber(stats.emailedCount) }
        );

  return (
    <>
      <div className={styles.released}>
        <span className={styles.releasedTick}>
          <FontAwesomeIcon icon={faCheck} />
        </span>
        <div>
          <h3 className={styles.releasedTitle}>{headline}</h3>
          <p className={styles.releasedNote}>
            {intl.formatMessage(
              {
                id: 'app.sml.presave.released.note',
                defaultMessage:
                  '{days, plural, =0 {Out today} one {Out # day} other {Out # days}} · {emails, plural, =0 {no reminder emails} one {# reminder email sent} other {# reminder emails sent}}',
              },
              { days: daysBetween(releaseDate, Date.now()), emails: stats.emailedCount }
            )}
          </p>
        </div>
      </div>

      <div className={styles.counters}>
        <PresaveCounterCard
          label={intl.formatMessage({ id: 'app.sml.presave.released.delivered', defaultMessage: 'Delivered' })}
          value={intl.formatNumber(stats.executedCount)}
          delta={
            deliveredShare === null
              ? undefined
              : intl.formatNumber(deliveredShare, { style: 'percent', maximumFractionDigits: 1 })
          }
          deltaIsPositive
        >
          {stats.failedCount > 0 && (
            <p className={styles.cardNote}>
              {intl.formatMessage(
                {
                  id: 'app.sml.presave.released.failed',
                  defaultMessage: '{count} failed — revoked tokens or removed accounts.',
                },
                { count: intl.formatNumber(stats.failedCount) }
              )}
            </p>
          )}
          {stats.needsReauthCount > 0 && (
            <p className={styles.cardNote}>
              {intl.formatMessage(
                {
                  id: 'app.sml.presave.released.needs_reauth',
                  defaultMessage: '{count} lost their connection and were emailed instead.',
                },
                { count: intl.formatNumber(stats.needsReauthCount) }
              )}
            </p>
          )}
        </PresaveCounterCard>

        <PresaveCounterCard
          label={intl.formatMessage({ id: 'app.sml.presave.released.emailed', defaultMessage: 'Emailed' })}
          value={intl.formatNumber(stats.emailedCount)}
        >
          <p className={styles.cardNote}>
            {intl.formatMessage(
              {
                id: 'app.sml.presave.released.emailed_note',
                defaultMessage: 'One email each, with the links you gave us. {count} are on your news list.',
              },
              { count: intl.formatNumber(stats.optInNewsCount) }
            )}
          </p>
        </PresaveCounterCard>
      </div>
    </>
  );
};

export const PresaveReleasedSummary = TypedMemo(PresaveReleasedSummaryComponent);
